import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roomApi } from '../services/api';
import { accountApi, authStore } from '../services/accountApi';
import { Team } from '../types/team';
import { CreateRoomRequest, DeckType, DECK_CARDS, JoinRoomRequest } from '../types';
import { getFormattedRandomQuote } from '../utils/quotes';
import './Home.css';
import { apiErrorMessage } from '../utils/apiError';
import { saveRoomIdentity } from '../utils/roomIdentity';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [joinRoomId, setJoinRoomId] = useState('');
  const [joinDisplayName, setJoinDisplayName] = useState('');
  const [createRoomData, setCreateRoomData] = useState<CreateRoomRequest>({
    name: '',
    display_name: '',
    deck_type: 'fibonacci',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inspirationalQuote, setInspirationalQuote] = useState('');
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const navigate = useNavigate();

  // ページ読み込み時と言語変更時に名言を設定
  useEffect(() => {
    const currentLanguage = t('__language__');
    setInspirationalQuote(getFormattedRandomQuote(currentLanguage));
  }, [t]);

  // ログイン中ならチーム一覧を取得（結果の記録先として選択できるようにする）
  useEffect(() => {
    if (!authStore.isLoggedIn()) return;
    accountApi.listTeams()
      .then(setMyTeams)
      .catch(() => setMyTeams([]));
  }, []);


  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await roomApi.createRoom({
        ...createRoomData,
        team_id: selectedTeamId || undefined,
      });
      // 作成されたルームが正常かどうかを確認してからナビゲート
      if (response.room && response.room.id) {
        // ルーム作成者として自動参加するため、user_idをルーム単位で保存
        saveRoomIdentity(response.room.id, response.user_id, createRoomData.display_name);
        navigate(`/room/${response.room.id}`);
      } else {
        setError(t('errors.roomCreationFailed') || 'Room creation failed');
      }
    } catch (err: any) {
      console.error('Room creation error:', err);
      setError(apiErrorMessage(err, t('errors.roomCreationFailed') || 'Failed to create room'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinRoomId.trim() || !joinDisplayName.trim()) return;
    
    setError('');
    setIsLoading(true);

    try {
      const joinData: JoinRoomRequest = { display_name: joinDisplayName };
      const result = await roomApi.joinRoom(joinRoomId, joinData);
      saveRoomIdentity(joinRoomId, result.user_id, joinDisplayName);
      navigate(`/room/${joinRoomId}`);
    } catch (err: any) {
      console.error('Join room error:', err);
      if (err.response?.status === 404) {
        setError(t('errors.roomNotFound') || 'Room not found');
      } else {
        setError(apiErrorMessage(err, t('errors.joinRoomFailed') || 'Failed to join room'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRoomChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCreateRoomData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="home-container">
      <div className="home-content">
        <h1 className="home-title">{t('home.title') || 'Planning Poker'}</h1>
        <p className="home-description">
          {inspirationalQuote}
        </p>

        <div className="actions-container">
          <div className="action-card">
            <h2>{t('home.createRoom.title') || 'Create New Room'}</h2>
            <form onSubmit={handleCreateRoom} className="room-form">
              <div className="form-group">
                <label htmlFor="displayName">{t('home.createRoom.displayName') || 'Display Name:'}</label>
                <input
                  type="text"
                  id="displayName"
                  name="display_name"
                  value={createRoomData.display_name}
                  onChange={handleCreateRoomChange}
                  required
                  className="form-input"
                  placeholder={t('home.createRoom.displayNamePlaceholder') || ''}
                />
              </div>
              <div className="form-group">
                <label htmlFor="roomName">{t('home.createRoom.roomName') || 'Room Name:'}</label>
                <input
                  type="text"
                  id="roomName"
                  name="name"
                  value={createRoomData.name}
                  onChange={handleCreateRoomChange}
                  required
                  className="form-input"
                  placeholder={t('home.createRoom.roomNamePlaceholder') || ''}
                />
              </div>
              <div className="form-group">
                <label htmlFor="deckType">{t('home.createRoom.deckType') || 'Card Deck:'}</label>
                <select
                  id="deckType"
                  name="deck_type"
                  value={createRoomData.deck_type}
                  onChange={handleCreateRoomChange}
                  className="form-select"
                >
                  <option value="fibonacci">{t('home.createRoom.deckOptions.fibonacci')}</option>
                  <option value="powers_of_2">{t('home.createRoom.deckOptions.powersOf2')}</option>
                  <option value="tshirt">{t('home.createRoom.deckOptions.tshirt')}</option>
                </select>
              </div>
              {myTeams.length > 0 && (
                <div className="form-group">
                  <label htmlFor="teamSelect">結果の記録先チーム（任意）:</label>
                  <select
                    id="teamSelect"
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">記録しない</option>
                    {myTeams.map((team) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="action-button create-button"
              >
{isLoading ? (t('home.createRoom.creating') || 'Creating...') : (t('home.createRoom.createButton') || 'Create Room')}
              </button>
            </form>
          </div>

          <div className="action-card">
            <h2>{t('home.joinRoom.title') || 'Join Existing Room'}</h2>
            <form onSubmit={handleJoinRoom} className="room-form">
              <div className="form-group">
                <label htmlFor="joinDisplayName">{t('home.joinRoom.displayName') || 'Display Name:'}</label>
                <input
                  type="text"
                  id="joinDisplayName"
                  value={joinDisplayName}
                  onChange={(e) => setJoinDisplayName(e.target.value)}
                  required
                  className="form-input"
                  placeholder={t('home.joinRoom.displayNamePlaceholder') || ''}
                />
              </div>
              <div className="form-group">
                <label htmlFor="roomId">{t('home.joinRoom.roomId') || 'Room ID:'}</label>
                <input
                  type="text"
                  id="roomId"
                  value={joinRoomId}
                  onChange={(e) => setJoinRoomId(e.target.value)}
                  required
                  className="form-input"
                  placeholder={t('home.joinRoom.roomIdPlaceholder') || ''}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="action-button join-button"
              >
{isLoading ? (t('home.joinRoom.joining') || 'Joining...') : (t('home.joinRoom.joinButton') || 'Join Room')}
              </button>
            </form>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="deck-preview">
          <h3>{t('home.deckPreview.title') || 'Card Deck Preview'}</h3>
          <div className="deck-cards">
            {DECK_CARDS[createRoomData.deck_type as DeckType].map((card, index) => (
              <div key={index} className="preview-card">
                {card}
              </div>
            ))}
          </div>
        </div>

        <div className="feedback-section">
          <a
            href="https://forms.gle/e6r221urJB3A76sq9"
            target="_blank"
            rel="noopener noreferrer"
            className="feedback-card"
          >
            <h3>{t('home.feedback.title') || 'ご意見・ご要望'}</h3>
            <p>{t('home.feedback.description') || 'アプリの改善のため、ご意見をお聞かせください'}</p>
          </a>
        </div>

      </div>
    </div>
  );
};

export default Home;